// Mock data for ZenCap Fintech Landing Page

export const navigationItems = [
  { label: 'Home', href: '#home' },
  { label: 'Intelligence', href: '#solution' },
  { label: 'Features', href: '#advantages' },
  { label: 'How it Works', href: '#how-it-works' },
  { label: 'FAQ', href: '#faq' }
];

export const heroData = {
  title: 'Make Every Financial Move With Confidence.',
  description: 'AI-powered insights and expert-verified guidance to help you take smarter financial decisions—securely and responsibly.',
  buttonText: 'Start Making Better Decisions',
  trustedClients: '10k+',
  clientsText: 'Early Adopters',
  rating: '4.9',
  ratingText: 'Goal Accuracy'
};

export const clientLogos = [
  { id: 1, name: 'Setu', logo: 'https://cdn.worldvectorlogo.com/logos/visa-2.svg' }, // Placeholder for Indian partners
  { id: 2, name: 'Razorpay', logo: 'https://cdn.worldvectorlogo.com/logos/mastercard-2.svg' },
  { id: 3, name: 'SEBI-RIA', logo: 'https://cdn.worldvectorlogo.com/logos/stripe-4.svg' }
];

export const features = [
  {
    id: 1,
    tag: 'Decision Intelligence',
    title: 'ADI: Beyond Passive Dashboards',
    description: 'Autonomous Decision Intelligence analyzes patterns and risk preferences to generate explainable recommendations and behavioral nudges.',
    stats: { amount: '₹12,450', currency: 'INR', gain: 'Optimized via ADI' }
  },
  {
    id: 2,
    tag: 'Financial Health',
    title: 'Financial Health Index (FHI)',
    description: 'A real-time metric that grounds every recommendation in your actual financial context and long-term objectives.',
    stats: { amount: '82', currency: 'Points', period: 'Current FHI Score' }
  },
  {
    id: 3,
    tag: 'Compliance-First',
    title: 'SEBI-RIA Integration',
    description: 'Read-only data access with strictly user-consented flows. Integrated RIA-led advisory for regulated financial planning.',
    stats: { amount: '100%', growth: 'Compliant', avgScore: 'Read-Only Access' }
  }
];

export const howItWorksData = {
  title: 'How ZenCap Works',
  subtitle: 'Intelligent Guidance. Regulated Human Support.',
  sections: [
    {
      title: 'AI-Powered Decision Intelligence',
      description: 'At the core of ZenCap is its AI-driven decision intelligence system, providing context-aware insights, goal-aligned recommendations, and behavioral nudges.',
      disclaimer: 'ZenCap’s AI does not execute transactions. It exists to support better decision-making — not replace user judgment.',
      points: [
        'Context-aware financial insights',
        'Goal-aligned recommendations',
        'Behavioral nudges for discipline',
        'Clear explanations for every suggestion'
      ]
    },
    {
      title: 'Regulated Human Advisory',
      description: 'For users who need deeper personalization, ZenCap offers opt-in 1-on-1 financial advisory services via licensed professionals.',
      disclaimer: 'Advisory is delivered only by SEBI-registered advisors with explicit user consent.',
      points: [
        'Suitability assessments',
        'Full disclosures & documentation',
        'Licensed advisor responsibility',
        'Regulated activity compliance'
      ]
    }
  ],
  roles: [
    { actor: 'ZenCap AI', role: 'Decision intelligence and insights' },
    { actor: 'SEBI-Registered Advisors', role: 'Personalized financial advice' },
    { actor: 'User', role: 'Full control over decisions and actions' }
  ]
};

export const targetAudience = {
  title: 'Who ZenCap Is For',
  description: 'Built for individuals who want clarity, confidence, and responsible guidance.',
  profiles: [
    { type: 'Salaried Professionals', icon: 'briefcase' },
    { type: 'Freelancers & Gig Workers', icon: 'laptop' },
    { type: 'First-time Investors', icon: 'trending-up' },
    { type: 'Growing Investors', icon: 'bar-chart' }
  ]
};

export const benefitsData = {
  tag: 'Clarity, Not Complexity',
  title: 'Understand. Decide. Learn.',
  description: 'ZenCap helps you understand your financial situation, decide what actions make sense, and learn why those actions matter.',
  buttonText: 'Join the Revolution',
  checkItems: ['Read-only, user-consented data', 'No automated execution', 'Transparent audit trails']
};

export const advantages = [
  {
    id: 1,
    icon: 'zap',
    title: 'Context-Aware Insights',
    description: 'Explainable AI prompts that help you avoid impulse spending and hit savings goals.'
  },
  {
    id: 2,
    icon: 'shield',
    title: 'Compliance-First Architecture',
    description: 'Strictly user-consented flows with clear separations between technology and regulation.'
  },
  {
    id: 3,
    icon: 'brain',
    title: 'Explainable Recommendations',
    description: 'A system that learns from your behavior to provide the "why" behind every financial move.'
  },
  {
    id: 4,
    icon: 'layout',
    title: 'User-Led Control',
    description: 'No discretionary authority. You maintain full control over all decisions and execution.'
  },
  {
    id: 5,
    icon: 'link',
    title: 'Licensed Advisory',
    description: 'Personalized advisory delivered exclusively by SEBI-registered RIA partners.'
  },
  {
    id: 6,
    icon: 'lock',
    title: 'Secure Data Access',
    description: 'Secure, read-only access to your financial data with transparent consent flows.'
  }
];

export const nextGenData = {
  title: 'Scaling Responsible Intelligence',
  description: 'A dedicated platform combining AI-driven intelligence with human oversight, prioritizing trust and regulatory alignment.',
  features: [
    { title: 'Goal-Aligned Nudges', description: 'Behaviors that move you closer to your objectives.' },
    { title: 'Capital-Efficient Scaling', description: 'Powering RIAs with best-in-class technology.' }
  ]
};

export const pricingPlans = [
  {
    id: 1,
    name: 'Free Forever',
    price: '0',
    period: 'per month',
    description: 'Ideal for getting started with basics and the FHI index.',
    features: [
      'Financial Health Index (FHI)',
      'Basic Expense Analysis',
      'Read-Only Data Integration',
      'Goal Tracking',
      'Community Insights'
    ],
    badge: 'Experience Clarity',
    featured: false
  },
  {
    id: 2,
    name: 'ADI Plus',
    price: '299',
    period: 'per month',
    description: 'Unlock the power of Autonomous Decision Intelligence.',
    features: [
      'Advanced ADI Nudges',
      'Explainable Recommendations',
      'Behavioral Insights',
      'Unlimited Goal Management',
      'Priority Support'
    ],
    badge: 'Decision Support',
    featured: true
  },
  {
    id: 3,
    name: 'RIA Guided',
    price: 'Custom',
    period: 'Consultation',
    description: 'Expert human advisory integrated with AI precision.',
    features: [
      'Human-in-the-Loop Advisory',
      'SEBI-Registered Partners',
      'Personalized Planning',
      'Detailed Compliance Reports',
      'Full Audit Trails'
    ],
    badge: 'Maximum Trust',
    featured: false
  }
];

export const faqItems = [
  {
    id: 1,
    question: 'Is ZenCap an automated trading bot?',
    answer: 'No. ZenCap is an assistive decision-support system, not a discretionary execution engine. ZenCap’s AI does not execute transactions. It exists to support better decision-making — not replace user judgment.'
  },
  {
    id: 2,
    question: 'How do you handle my financial data securely?',
    answer: 'ZenCap is built with a compliance-first architecture. All data access is read-only and strictly based on your explicit consent. Your data remains yours.'
  },
  {
    id: 3,
    question: 'Who are ZenCap\'s financial advisors?',
    answer: 'Where personalized advisory is offered, it is delivered exclusively through qualified, SEBI-registered advisors (RIA) with explicit user consent and full suitability assessment.'
  },
  {
    id: 4,
    question: 'What is the separation of roles in ZenCap?',
    answer: 'ZenCap AI provides decision intelligence, SEBI-registered advisors provide personalized financial advice, and you (the user) retain full control over all decisions and actions.'
  },
  {
    id: 5,
    question: 'Who is ZenCap built for?',
    answer: 'ZenCap is built for individuals who want clarity and confidence: salaried professionals, freelancers, and first-time or growing investors seeking responsible guidance.'
  }
];

export const testimonials = [
  {
    id: 1,
    name: 'Anirudh Kumar',
    role: 'Tech Lead',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop',
    quote: 'ZenCap finally answered the question I ask every morning: what should I do with my surplus? The ADI nudges are scarily accurate.'
  },
  {
    id: 2,
    name: 'Priya Sharma',
    role: 'Freelancer',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop',
    quote: 'As a freelancer with irregular income, the Financial Health Index helps me know exactly when I can afford to invest and when to save.'
  },
  {
    id: 3,
    name: 'Rohan Mehta',
    role: 'First-time Investor',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop',
    quote: 'The clarity I get from ZenCap is unmatched. It doesn’t just show charts; it explains the "why" behind every financial move.'
  }
];

export const blogPosts = [
  {
    id: 1,
    title: 'Why Decision Intelligence is the Next Frontier in Fintech',
    date: 'January 24, 2026',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop'
  },
  {
    id: 2,
    title: 'Understanding ADI: The Future of Responsible AI in Finance',
    date: 'February 05, 2026',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop'
  },
  {
    id: 3,
    title: 'How our proprietary FHI metric measures financial success',
    date: 'February 12, 2026',
    image: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=400&h=300&fit=crop'
  }
];

export const ctaData = {
  title: 'Ready for Clarity, Not Complexity?',
  description: "Join thousands of Indian professionals getting structured, AI-driven guidance for their financial future.",
  buttonText: 'Get Started with ZenCap',
  features: ['Compliance-First', 'AI + Human Oversight']
};

export const footerData = {
  companyName: 'Xalyte Technologies Pvt. Ltd.',
  companyLink: 'https://xalyte.in/',
  description: 'ZenCap is an AI-powered Financial Decision Intelligence Platform that helps users make better financial decisions using user-consented data, with all regulated activities handled by licensed partners.',
  services: [
    'Decision Layer',
    'FHI Index',
    'Goal Planning',
    'RIA Advisory',
    'Compliance Engine'
  ],
  address: 'Level 4, Fintech Tower, BKC, Mumbai, India',
  email: 'hello@zencap.in',
  phone: '+91 (022) 4567 8900',
  copyright: '© 2026 ZenCap - All rights reserved.',
  links: ['Home', 'About', 'Privacy Policy', 'Terms of Service'],
  socialLinks: {
    instagram: 'https://www.instagram.com/zencap.in?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==',
    twitter: 'https://x.com/ZenCapOfficial',
    linkedin: 'https://www.linkedin.com/company/xalyte/'
  }
};

export const clientAvatars = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=50&h=50&fit=crop',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop',
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=50&h=50&fit=crop'
];